/**
 * ClaimOptionListTab - generic management surface for an entity-attribute
 * option list (a SELECT attribute's `configuration_data.options`).
 *
 * This intentionally mirrors and exposes the existing entity-attribute API
 * (the same one the Entity Editor writes through): it resolves the owning
 * EntityType by key, finds the target attribute by code_name, and edits its
 * `configuration_data.options` via PATCH. It does not introduce a bespoke
 * endpoint, and the Entity Editor remains the canonical schema surface.
 *
 * Reused by the Payment Types, Units of Measure, and Labels tabs under
 * Settings > Claims. Each instance is RBAC-gated by a `<prefix>.{view,
 * create,edit,delete}` codename (seeded in seed_permissions.py).
 */

import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ListChecks, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useEntityTypeByKey,
  useUpdateEntityAttribute,
} from '@/admin/hooks/use-entities'
import {
  useClaimSchemas,
  useUpdateClaimAttribute,
} from '@/admin/hooks/use-claim-schemas'

// Projection metadata keys added by the backend when an attribute is
// mirrored from a bespoke source table (e.g. ClaimAttribute). These must
// be stripped before writing back to the source so they are not persisted
// into the source config and re-duplicated on the next projection.
const PROJECTION_KEYS = [
  'value_source',
  'source_model',
  'source_attribute_uuid',
  'source_owner_uuid',
  'source_owner_key',
  'source_owner_label',
] as const
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  ColorPicker,
  ColorSwatch,
  DEFAULT_COLOR_PRESETS,
} from '@/shared/components/ui/color-picker'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { Label } from '@/shared/components/ui/label'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

export type ClaimOption = {
  label: string
  value: string
  color?: string
  [key: string]: unknown
}

export interface ClaimOptionListTabProps {
  /** EntityType key that owns the attribute (e.g. `claim_payment`). */
  entityKey: string
  /** Attribute code_name whose options are managed (e.g. `payment_type`). */
  attributeCode: string
  /** Permission codename prefix (e.g. `payment-types`). */
  permissionPrefix: string
  eyebrow: string
  title: string
  description: string
  /** Singular noun used in buttons / toasts (e.g. `payment type`). */
  noun: string
  /**
   * When true, each option carries a `color` (hex) chosen via the shared
   * ColorPicker and shown as a swatch. Used by the Labels tab.
   */
  withColor?: boolean
}

function deriveValue(label: string): string {
  return label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function ClaimOptionListTab({
  entityKey,
  attributeCode,
  permissionPrefix,
  eyebrow,
  title,
  description,
  noun,
  withColor = false,
}: ClaimOptionListTabProps) {
  const { can } = usePermissions()
  const canCreate = can(`${permissionPrefix}.create`)
  const canEdit = can(`${permissionPrefix}.edit`)
  const canDelete = can(`${permissionPrefix}.delete`)

  const { data: entity, isLoading } = useEntityTypeByKey(entityKey)
  const attribute = useMemo(
    () => entity?.attributes.find((a) => a.code_name === attributeCode),
    [entity, attributeCode],
  )
  const options = useMemo<ClaimOption[]>(
    () => (attribute?.configuration_data.options as ClaimOption[]) ?? [],
    [attribute],
  )

  const qc = useQueryClient()
  const updateEntityAttr = useUpdateEntityAttribute(entity?.uuid)
  const updateClaimAttr = useUpdateClaimAttribute()
  const isPending = updateEntityAttr.isPending || updateClaimAttr.isPending
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<ClaimOption | null>(null)
  const [deleting, setDeleting] = useState<ClaimOption | null>(null)

  // Attributes on the `claim` entity are projected from ClaimAttribute
  // rows; editing the registry mirror would be wiped on the next
  // projection. When the attribute carries claim_attribute provenance we
  // write back to the bespoke ClaimAttribute source instead. Native
  // registry attributes (claim_payment.payment_type, settlement_line_item
  // .uom) are edited directly via the entity-attribute API.
  const config = attribute?.configuration_data ?? {}
  const isClaimAttr = config.value_source === 'claim_attribute'

  // A ClaimAttribute can live on several schemas (OC has 4 claim
  // schemas) and the registry merges their options into one mirror.
  // Edits must fan out to every schema's row so the option list stays
  // consistent and deletes propagate. Only fetched/used for the
  // claim-attribute case.
  const { data: claimSchemas = [] } = useClaimSchemas()
  const claimAttrTargets = useMemo(() => {
    if (!isClaimAttr) return []
    return claimSchemas.flatMap((schema) =>
      schema.attributes
        .filter((a) => a.code_name === attributeCode)
        .map((a) => ({
          schemaUuid: schema.uuid,
          attrUuid: a.uuid,
          config: a.configuration_data,
        })),
    )
  }, [isClaimAttr, claimSchemas, attributeCode])

  async function saveOptions(next: ClaimOption[]): Promise<void> {
    if (!attribute) return
    if (isClaimAttr) {
      if (claimAttrTargets.length === 0) {
        // Schemas not loaded yet - surface a real failure rather than
        // silently reporting success without writing anything.
        throw new Error('Claim schemas are still loading. Try again.')
      }
      // Sequential, not parallel: each PUT re-syncs the claim entity
      // catalog server-side, and concurrent syncs on the same entity
      // would race.
      for (const target of claimAttrTargets) {
        const sourceConfig: Record<string, unknown> = { ...target.config }
        for (const key of PROJECTION_KEYS) delete sourceConfig[key]
        await updateClaimAttr.mutateAsync({
          schemaUuid: target.schemaUuid,
          attrUuid: target.attrUuid,
          // field_type must accompany configuration_data so the
          // serializer can validate the options against the field type.
          data: {
            field_type: attribute.field_type,
            configuration_data: { ...sourceConfig, options: next },
          },
        })
      }
      qc.invalidateQueries({ queryKey: ['entity-type'] })
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      return
    }
    await updateEntityAttr.mutateAsync({
      attrUuid: attribute.uuid,
      configuration_data: { ...config, options: next },
    })
  }

  const columns = useMemo<DataTableColumn<ClaimOption>[]>(
    () => [
      ...(withColor
        ? [
            {
              id: 'color',
              header: '',
              cell: (row: ClaimOption) => <ColorSwatch color={row.color} />,
            } as DataTableColumn<ClaimOption>,
          ]
        : []),
      {
        id: 'label',
        header: 'Label',
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink)]">{row.label}</span>
        ),
      },
      {
        id: 'value',
        header: 'Value',
        cell: (row) => (
          <span className="font-mono text-[11px] text-[var(--ink-3)]">
            {row.value}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <div
            className={
              canEdit || canDelete
                ? 'flex items-center justify-end gap-1'
                : 'hidden'
            }
          >
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className={canEdit ? undefined : 'hidden'}
              onClick={(event) => {
                event.stopPropagation()
                setEditing(row)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={isPending}
              className={canDelete ? undefined : 'hidden'}
              onClick={(event) => {
                event.stopPropagation()
                setDeleting(row)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, canDelete, options, isPending, noun, withColor],
  )

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="clara-label text-[var(--ink-3)]">{eyebrow}</p>
          <h2 className="mt-1 text-[18px] text-[var(--ink)]">{title}</h2>
          <p className="mt-1 max-w-[720px] text-[12px] leading-relaxed text-[var(--ink-3)]">
            {description}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={!canCreate || !attribute}
        >
          <Plus className="h-3.5 w-3.5" />
          Add {noun}
        </Button>
      </div>

      {!isLoading && !attribute ? (
        <EmptyState
          icon={ListChecks}
          title="Not configured"
          description={`The ${noun} list is not set up for this tenant yet. Configure the ${attributeCode} attribute in the Entity Editor first.`}
        />
      ) : (
        <DataTable<ClaimOption>
          tableId={`claim-options-${permissionPrefix}`}
          columns={columns}
          data={options}
          totalCount={options.length}
          isLoading={isLoading}
          rowKey={(row) => row.value}
          emptyStateNode={
            <EmptyState
              icon={ListChecks}
              title={`No ${noun}s`}
              description={`Add the first ${noun} so it appears in the claim forms.`}
            />
          }
        />
      )}

      <OptionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        noun={noun}
        existing={options}
        withColor={withColor}
        onSave={async (label, value, color) => {
          await saveOptions([
            ...options,
            { label, value, ...(withColor ? { color } : {}) },
          ])
        }}
      />
      <OptionDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        noun={noun}
        editing={editing}
        existing={options}
        withColor={withColor}
        onSave={async (label, _value, color) => {
          await saveOptions(
            options.map((o) =>
              o.value === editing?.value
                ? { ...o, label, ...(withColor ? { color } : {}) }
                : o,
            ),
          )
        }}
      />
      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={`Delete ${noun}?`}
        description={
          deleting
            ? `"${deleting.label}" will be removed from the ${noun} list. Existing records that already use it are not changed.`
            : undefined
        }
        isPending={isPending}
        onConfirm={() => {
          const target = deleting
          if (!target) return
          void saveOptions(options.filter((o) => o.value !== target.value)).then(
            () => {
              toast.success(`Removed ${noun}`)
              setDeleting(null)
            },
            (err) => toastApiError(err, `Failed to remove ${noun}`),
          )
        }}
      />
    </div>
  )
}

function OptionDialog({
  open,
  onOpenChange,
  noun,
  existing,
  editing,
  withColor = false,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  noun: string
  existing: ClaimOption[]
  editing?: ClaimOption | null
  withColor?: boolean
  onSave: (label: string, value: string, color?: string) => Promise<void>
}) {
  const isEdit = Boolean(editing)
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Label',
        value: editing?.label ?? '',
        required: 'Label is required',
        placeholder: `e.g. ${noun === 'unit of measure' ? 'Each' : 'Labor'}`,
      },
      ...(withColor
        ? [
            {
              type: 'custom' as const,
              name: 'color',
              label: 'Colour',
              value: editing?.color ?? DEFAULT_COLOR_PRESETS[0].value,
              render: (ctx) => (
                <div className="flex flex-col gap-1.5">
                  <Label>Colour</Label>
                  <ColorPicker
                    value={String(ctx.values.color ?? '')}
                    onChange={(color) => ctx.setFieldValue('color', color)}
                  />
                </div>
              ),
            } satisfies FormField,
          ]
        : []),
    ],
    [editing, noun, withColor],
  )

  const { Form } = useForm(
    {
      name: `claim-option-form-${isEdit ? 'edit' : 'add'}`,
      submit: 'footer',
      fields,
    },
    async (values) => {
      const label = String(values.label ?? '').trim()
      if (!label) return
      const value = editing?.value ?? deriveValue(label)
      if (
        !isEdit &&
        existing.some((o) => o.value === value || o.label === label)
      ) {
        toast.error(`That ${noun} already exists`)
        return
      }
      const color = withColor
        ? String(values.color ?? '').trim() || undefined
        : undefined
      try {
        await onSave(label, value, color)
        toast.success(isEdit ? `Updated ${noun}` : `Added ${noun}`)
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, `Failed to save ${noun}`)
      }
    },
    [editing, existing, isEdit, noun, onOpenChange, onSave, withColor],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${noun}` : `Add ${noun}`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the label shown in claim forms. The stored value cannot change.'
              : 'The stored value is generated from the label and used by existing records.'}
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" form={`claim-option-form-${isEdit ? 'edit' : 'add'}`}>
            {isEdit ? 'Save changes' : `Add ${noun}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
